import { Component, FormEvent } from 'react';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, ISerologyBloodDrawsResponse, ReportActionCreators } from '@nkr/store/index';
import { ISerologyBloodDraw } from '@nkr/models/index';
import { Button, FormControl, Well } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Grid from './grid';
import { Utility } from '@nkr/services/utility';
import { downloadFile } from '@nkr/services/download-file';

interface IOwnProps {
    isCenterLogin: boolean,
    centerId: number
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getSerologyBloodDraws: (centerId: number, startDate: string) => Promise<ISerologyBloodDrawsResponse>,
    getSerologyBloodDrawResults: (id: number) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: ISerologyBloodDraw[],
    startDate: string
}

class SerologyBloodDraws extends Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        startDate: moment().startOf('month').format('YYYY-MM-DD')
    }

    componentDidMount = () => {
        this.getData();
    }

    handleMonthChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let selected = moment(e.currentTarget.value, 'YYYY-MM-DD');

        this.setState({
            startDate: selected.format('YYYY-MM-DD')
        }, () => {
            this.getData();
        });
    }

    getData = () => {
        this.props.showLoading('Getting serology blood draw records, one moment please...').then(() => {
            this.props.getSerologyBloodDraws(this.props.centerId, this.state.startDate).then(response => {
                this.setState({
                    data: response.data.results
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    handleViewResults = (row: ISerologyBloodDraw) => {
        this.props.getSerologyBloodDrawResults(row.id).then((response: any) => {
            downloadFile(response.data, `serology_${row.donorAlias}.pdf`, 'application/pdf');
        }).catch(() => {
            toast.error('Unable to download serology results at this time. Please contact support.');
        });
    }

    handleExport = () => {
        let csvContent = 'data:text/csv;charset=utf-8,'
            + 'Donor Center,Donor Alias,Recipient Alias,Donation Date,Blood Draw Date,Results Date\n'
            + this.state.data.map(e => e.donorCenterAlias
                + ',' + e.donorAlias
                + ',' + e.recipientAlias
                + ',' + e.scheduledDonationDate
                + ',' + e.bloodDrawDate
                + ',' + e.resultsDate).join('\n');

        let encodedUri = encodeURI(csvContent);

        let link = document.createElement('a');

        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `serology-blood-draws.csv`);

        document.body.appendChild(link);

        link.click();
    }

    render = () => {
        const dateList = Utility.getRange(moment().subtract(11, 'month'), moment().add(1, 'month'), 'month').map(d => d.date(1));

        return (
            <>
                <Well bsSize='sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ marginRight: 5 }}>Donor Surgery Month:</div>
                        <div>
                            <FormControl
                                componentClass='select'
                                bsSize='sm'
                                value={this.state.startDate}
                                onChange={this.handleMonthChange}>
                                {dateList.map(date =>
                                    <option key={`ctr-${date.format('YYYY-MM-DD')}`} value={date.format('YYYY-MM-DD')}>{date.format('MM/YYYY')}</option>
                                )}
                            </FormControl>
                        </div>
                    </div>

                    <div>
                        <Button bsSize='sm' onClick={this.handleExport}>Export to CSV</Button>
                    </div>
                </Well>

                <Grid data={this.state.data} onViewResults={this.handleViewResults} />
            </>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getSerologyBloodDraws: (centerId, startDate) => {
            return dispatch(ReportActionCreators.getSerologyBloodDraws(centerId, startDate));
        },
        getSerologyBloodDrawResults: (id) => {
            return dispatch(ReportActionCreators.getSerologyBloodDrawResults(id));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(SerologyBloodDraws);
